import { MouseEventHandler } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/router';

import NumberWithTooltip from '@/components/shared/tooltips/NumberTooltip';
import { displayRepay } from '@/utils/formatter';
import { cn } from '@/utils/helper';

type DetailProps = {
  value: string | number | React.ReactNode;
  symbol?: string;
  label: string;
  className?: string;
};

interface ILoanGridItemView {
  name: string;
  id?: number | string;
  hideId?: boolean;
  loanDetails: DetailProps[];
  loanInfo?: DetailProps[];
  image: string | null;
  checked?: boolean;
  onClick?: () => void;
  checkboxShow?: boolean;
  handleAction?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  hideButton?: boolean;
  actionButtons?: boolean;
  activeOffersCount?: number;
  btnText?: string;
  tag?: string;
  nextImage?: boolean;
}

const labelsWithTooltip = ['APR', 'Repay', 'Principal', 'Loan Offer', 'Floor', 'Trait'];
const LoanInfoDetail = ({ label, value, symbol = '', className }: DetailProps & { value: string | number }) => {
  const fixedValue = displayRepay(value);
  const isNaNFixedValue = Number.isNaN(fixedValue);
  return (
    <div key={label} className={cn('flex flex-col', className)}>
      <p className='text-xs font-medium dark:!text-white !text-coal-250 leading-normal'>{label}</p>

      {labelsWithTooltip.includes(label) ? (
        <NumberWithTooltip fullNumber={value} symbol={symbol}>
          <p className='text-xs font-normal dark:!text-white !text-coal-250 leading-normal font-space-mono'>{`${isNaNFixedValue ? 'N/A' : fixedValue} ${isNaNFixedValue ? '' : symbol}`}</p>
        </NumberWithTooltip>
      ) : (
        <p className='text-xs font-normal dark:!text-white !text-coal-250 leading-normal font-space-mono'>
          {value} {symbol}
        </p>
      )}
    </div>
  );
};
const LoanInfo = ({ detail }: { detail: DetailProps[] }) => {
  return (
    <>
      {detail.map(({ label, value, symbol = '', className }) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return <LoanInfoDetail key={label} label={label} value={value} symbol={symbol} className={className} />;
        }
        return value;
      })}
    </>
  );
};

const LoanGridItemView: React.FC<ILoanGridItemView> = ({ handleAction, hideId = false, name, id, loanInfo, loanDetails, image, className, activeOffersCount, actionButtons, nextImage, hideButton = false, btnText = 'Borrow', tag, onClick }) => {
  const router = useRouter();

  const { rarity, withoutRarity } = loanDetails.reduce<{ withoutRarity: DetailProps[]; rarity: DetailProps | null }>(
    (agg, item) => {
      if (item.label === 'Rarity') {
        agg.rarity = item;
      } else {
        agg.withoutRarity.push(item);
      }

      return agg;
    },
    { withoutRarity: [], rarity: null }
  );

  return (
    <div onClick={onClick} className={cn('relative py-[25px] px-[23px] dark:bg-jet-black flex bg-white flex-col border-r border-y dark:border-coal-border min-w-[236px] justify-between', onClick && 'cursor-pointer', className ?? '')}>
      <div>
        <div className='flex items-center flex-col justify-center'>
          <h5 className='w-full text-base font-semibold text-coal-250 dark:text-white text-center truncate'>{name}</h5>
          {id && !hideId && <p className='w-full text-xs font-normal text-coal-250 dark:text-white text-center truncate'>#{id}</p>}
        </div>
        {loanInfo && (
          <div className='flex justify-between my-1'>
            <LoanInfo detail={loanInfo} />
          </div>
        )}
        <div className='relative mt-1 bg-white-100'>
          {!hideButton && <strong className='text-white text-xs font-semibold absolute top-[21px] bg-jet-black-100 px-2 py-1 rounded-r-sm'>{tag || ` Rarity ${rarity?.value || 0}`}</strong>}
          {!!activeOffersCount && <strong className='text-white text-xs font-semibold absolute top-5 left-0 bg-jet-black-100 px-2 py-1 rounded-r-sm'>{activeOffersCount} Offers</strong>}
          {nextImage ? <Image src={image || '/bayc-collectors.png'} className='h-[190px] w-full object-cover' alt='loan image' width={244} height={199} quality={100} /> : <img src={image || '/bayc-collectors.png'} className='h-[190px] w-full object-cover' alt='loan image' />}
        </div>
        <div className='flex justify-between mt-1'>
          <LoanInfo detail={withoutRarity} />
        </div>
      </div>
      {!hideButton && (
        <button className='mt-[15px] w-[86px] h-[39px] flex justify-center items-center rounded text-white text-xs font-semibold bg-primary mx-auto' onClick={handleAction}>
          {btnText}
        </button>
      )}
      {actionButtons && (
        <div className='flex w-full pt-3 justify-center items-center gap-3'>
          <button onClick={() => router.push(`/loans/loan-offers/${id}`)} className='bg-primary/15 text-primary dark:text-primary-blue-100 text-xs font-semibold w-[88px] h-[39px] flex justify-center items-center rounded'>
            View Offers
          </button>
          <button onClick={handleAction} className='bg-primary text-white text-xs font-semibold w-[87px] h-[39px] flex justify-center items-center rounded'>
            Make Offer
          </button>
        </div>
      )}
    </div>
  );
};

export default LoanGridItemView;
